import {
  RepositoryPicker,
  type RepositoryPickerOwner,
  type RepositoryPickerRepository,
} from '@github-ui/issues-repository-picker'
import {VisuallyHidden} from '@primer/react'
import {Suspense, useId} from 'react'

import {TEST_IDS} from './constants/test-ids'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {TemplateListLoading} from './TemplateListLoading'
import {TemplateListPaneFromRepo} from './TemplateListPane'
import type {IssueCreationKind} from './utils/model'

export type RepositoryAndTemplatePickerProps = {
  repository: RepositoryPickerRepository | undefined
  topRepositories: RepositoryPickerRepository[] | null
  owner?: RepositoryPickerOwner
  organization?: string
  preventAutoSelect?: boolean
  onRepositorySelected: (repository: RepositoryPickerRepository | undefined) => void
  onTemplateSelected: (filename: string, kind: IssueCreationKind) => void
}

export function RepositoryAndTemplatePicker({
  repository,
  topRepositories,
  owner,
  organization,
  preventAutoSelect,
  onRepositorySelected,
  onTemplateSelected,
}: RepositoryAndTemplatePickerProps) {
  const {optionConfig} = useIssueCreateConfigContext()
  const descriptionId = useId()
  const requiredDescriptionId = useId()
  const repoLeftMargin = optionConfig.insidePortal ? `tmp-ml-3` : `ml-0`
  // With the header switcher, repo selection lives in the dialog header, so the pane only shows
  // templates.
  const repoSelectionInHeader = optionConfig.showOrgRepositoryPicker

  return (
    <div className={optionConfig.insidePortal ? 'pt-2' : ''} data-testid={TEST_IDS.repositoryAndTemplateDialog}>
      {!repoSelectionInHeader && (
        <>
          <Field className={repoLeftMargin} name="Repository" />
          <VisuallyHidden id={requiredDescriptionId}>Required</VisuallyHidden>
          <div className={`${optionConfig.insidePortal ? `mb-2` : `tmp-mb-3`} ${repoLeftMargin}`}>
            <RepositoryPicker
              aria-describedby={`${requiredDescriptionId} ${descriptionId}`}
              initialRepository={repository}
              onSelect={onRepositorySelected}
              owner={owner}
              organization={organization}
              topRepositories={topRepositories}
              preventDefault={preventAutoSelect}
              focusRepositoryPicker
              enforceAtleastOneSelected
              options={{hasIssuesEnabled: true, includeForks: true}}
            />
          </div>
        </>
      )}
      <Suspense fallback={<TemplateListLoading />}>
        {repository && (
          <TemplateListPaneFromRepo
            owner={repository.owner.login}
            repo={repository.name}
            onTemplateSelected={onTemplateSelected}
            descriptionId={descriptionId}
          />
        )}
      </Suspense>
    </div>
  )
}

function Field({name, className}: {name: string; className?: string}) {
  return (
    <div className={`pt-1 pb-1 ${className}`}>
      <span className={'text-bold'}>{name}</span>
      <span className={'ml-1 mb-1 fgColor-danger'}>*</span>
    </div>
  )
}
